import React, { useState } from "react";
import { useParams } from "react-router-dom";
import Seo from "../Seo";
import cards from "../../data/cards.json";
import "../../Assets/css/BusinessCard.css";
import logo from "../../Assets/img/Navigation/nfg_logo.jpeg";

const BusinessCard = () => {
  const { slug } = useParams();
  const [showShare, setShowShare] = useState(false);
  const [copied, setCopied] = useState(false);
  const [zelleCopied, setZelleCopied] = useState(false);

  const person = cards.find((c) => c.slug === slug);

  if (!person) {
    return (
      <div className="card-page">
        <Seo
          title="Card Not Found | NeedForGeeks"
          description="No NeedForGeeks business card exists for this link."
          path={`/card/${slug}`}
          noindex
        />
        <div className="card-container">
          <div className="card-profile" style={{ textAlign: "center" }}>
            <h2 style={{ color: "#fff" }}>Card not found</h2>
            <p style={{ color: "rgba(255,255,255,0.6)" }}>
              No business card exists for "{slug}"
            </p>
          </div>
        </div>
      </div>
    );
  }

  const cardUrl = window.location.href;

  const handleShare = () => {
    if (navigator.share) {
      navigator.share({
        title: `${person.name} - ${person.company}`,
        text: `Contact ${person.name} at ${person.company}`,
        url: cardUrl,
      });
    } else {
      setShowShare(true);
    }
  };

  const copyLink = () => {
    navigator.clipboard.writeText(cardUrl).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      setShowShare(false);
    });
  };

  const saveContact = () => {
    const vcard = [
      "BEGIN:VCARD",
      "VERSION:3.0",
      `FN:${person.name}`,
      `ORG:${person.company}`,
      `TITLE:${person.title}`,
      `TEL;TYPE=CELL:${person.phone}`,
      `EMAIL:${person.email}`,
      person.website ? `URL:${person.website}` : "",
      "END:VCARD",
    ]
      .filter(Boolean)
      .join("\n");

    const blob = new Blob([vcard], { type: "text/vcard" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${person.name.replace(/\s+/g, "_")}.vcf`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const emailLink = `mailto:${person.email}`;
  const phoneLink = `tel:${person.phone.replace(/\s/g, "")}`;
  const smsLink = `sms:${person.phone.replace(/\s/g, "")}`;

  return (
    <div className="card-page">
      <Seo
        title={`${person.name} — ${person.title} | NeedForGeeks`}
        description={`${person.title} at ${person.company}${person.location ? ` · ${person.location}` : ""}. Save ${person.name}'s contact and get in touch.`}
        path={`/card/${person.slug}`}
        image={person.photo && /^https?:\/\//.test(person.photo) ? person.photo : undefined}
      />
      <div className="card-container">
        {/* Profile Section */}
        <div className="card-profile">
          <div className="card-logo-area">
            {person.photo ? (
              <img src={person.photo} alt={person.name} className="card-logo" />
            ) : (
              <img src={logo} alt={person.company} className="card-logo" />
            )}
          </div>

          <h1 className="card-name">{person.name}</h1>

          <div className="card-details">
            <p>{person.company}</p>
            <p>{person.location}</p>
            {person.university && <p>{person.university}</p>}
          </div>

          {person.tags && person.tags.length > 0 && (
            <div className="card-tags">
              {person.tags.map((tag, i) => (
                <span key={i} className="card-tag">
                  {tag}
                </span>
              ))}
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className="card-actions">
          <button className="card-btn-exchange" onClick={handleShare}>
            Exchange Contact
          </button>
          <button className="card-btn-save" onClick={saveContact} title="Save Contact">
            &#8615;
          </button>
        </div>

        {/* Contact Rows */}
        <div className="card-contact-list">
          <a href={phoneLink} className="card-contact-row">
            <div className="card-contact-icon">&#9742;</div>
            <div className="card-contact-info">
              <p className="card-contact-label">Call</p>
              <p className="card-contact-value">{person.phone}</p>
            </div>
            <span className="card-contact-arrow">&rarr;</span>
          </a>

          <a href={smsLink} className="card-contact-row">
            <div className="card-contact-icon">&#128241;</div>
            <div className="card-contact-info">
              <p className="card-contact-label">Text</p>
              <p className="card-contact-value">{person.phone}</p>
            </div>
            <span className="card-contact-arrow">&rarr;</span>
          </a>

          <a href={emailLink} className="card-contact-row">
            <div className="card-contact-icon">&#9993;</div>
            <div className="card-contact-info">
              <p className="card-contact-label">{person.title}</p>
              <p className="card-contact-value">{person.email}</p>
            </div>
            <span className="card-contact-arrow">&rarr;</span>
          </a>

          {person.website && (
            <a
              href={person.website}
              target="_blank"
              rel="noopener noreferrer"
              className="card-contact-row"
            >
              <div className="card-contact-icon">&#127760;</div>
              <div className="card-contact-info">
                <p className="card-contact-label">Website</p>
                <p className="card-contact-value">
                  {person.website.replace(/^https?:\/\//, "")}
                </p>
              </div>
              <span className="card-contact-arrow">&rarr;</span>
            </a>
          )}

          {person.zelle && (
            <div
              className="card-contact-row"
              onClick={() => {
                navigator.clipboard.writeText(person.zelle.email).then(() => {
                  setZelleCopied(true);
                  setTimeout(() => setZelleCopied(false), 2000);
                });
              }}
              style={{ cursor: "pointer" }}
            >
              <div className="card-contact-icon">&#36;</div>
              <div className="card-contact-info">
                <p className="card-contact-label">Zelle</p>
                <p className="card-contact-value">
                  {person.zelle.name}, {person.zelle.email}
                </p>
              </div>
              <span className="card-contact-arrow">{zelleCopied ? "Copied!" : "\u2192"}</span>
            </div>
          )}

          {person.businessCard && (
            <a
              href={person.businessCard}
              download
              className="card-contact-row"
            >
              <div className="card-contact-icon">&#128188;</div>
              <div className="card-contact-info">
                <p className="card-contact-label">Business Card</p>
                <p className="card-contact-value">Download</p>
              </div>
              <span className="card-contact-arrow">&darr;</span>
            </a>
          )}

          {/* Social Links */}
          {person.socials && (
            <div className="card-social-row">
              {person.socials.facebook && (
                <a
                  href={person.socials.facebook}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card-social-link"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                  </svg>
                </a>
              )}
              {person.socials.instagram && (
                <a
                  href={person.socials.instagram}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="card-social-link"
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
                  </svg>
                </a>
              )}
            </div>
          )}
        </div>

        {/* Share Modal (fallback for browsers without native share) */}
        {showShare && (
          <div className="card-share-overlay" onClick={() => setShowShare(false)}>
            <div className="card-share-modal" onClick={(e) => e.stopPropagation()}>
              <h3>Share Contact</h3>
              <div className="card-share-options">
                <button className="card-share-option" onClick={copyLink}>
                  &#128279; Copy Link
                </button>
                <button
                  className="card-share-option"
                  onClick={() => {
                    window.open(
                      `mailto:?subject=Contact%20${encodeURIComponent(person.name)}&body=${encodeURIComponent(cardUrl)}`,
                      "_blank"
                    );
                    setShowShare(false);
                  }}
                >
                  &#9993; Share via Email
                </button>
                <button
                  className="card-share-option"
                  onClick={() => {
                    window.open(
                      `sms:?body=${encodeURIComponent(`Check out ${person.name}'s contact card: ${cardUrl}`)}`,
                      "_blank"
                    );
                    setShowShare(false);
                  }}
                >
                  &#128172; Share via Text
                </button>
              </div>
              <button className="card-share-close" onClick={() => setShowShare(false)}>
                Close
              </button>
            </div>
          </div>
        )}

        {copied && <div className="card-copied-toast">Link copied!</div>}
      </div>
    </div>
  );
};

export default BusinessCard;
